/**
 * Trades Poster — Licences, Badges & Tickets
 * Source Item Type / Item Name catalogue (Configuration retrieves these values).
 */

export const COMPLIANCE_ITEM_TYPE_OPTIONS = [
  { value: 'badge', label: 'Badge' },
  { value: 'licence', label: 'Licence' },
  { value: 'trade_ticket', label: 'Ticket' },
];

/** Type order for Screen 1 grouping: Badge → Licence → Ticket */
export const COMPLIANCE_TYPE_SORT_ORDER = {
  badge: 0,
  licence: 1,
  trade_ticket: 2,
};

const ACTOR = 'Saim Sahi';
const CREATED_ON = '29 Jun 2026';
const UPDATED_ON = '29 Jun 2026';

function item(partial) {
  return {
    status: 'active',
    applied: false,
    hasSavedConfiguration: false,
    usedInCase: false,
    createdOn: CREATED_ON,
    createdBy: ACTOR,
    lastUpdatedOn: UPDATED_ON,
    lastUpdatedBy: ACTOR,
    ...partial,
  };
}

/**
 * Seed catalogue. IDs CI-0001–CI-0007 are stable for Configuration / Queue / Archive mocks.
 * CI-0008 is Gas Fitting (aligned with Compliance Archive & Queue samples).
 */
export const COMPLIANCE_ITEMS_CATALOG = [
  // Badges
  item({ id: 'CI-0001', name: 'Working With Children Check', complianceType: 'badge' }),
  item({ id: 'CI-0002', name: 'First Aid', complianceType: 'badge' }),
  item({
    id: 'CI-0009',
    name: 'Public Liability Insurance',
    complianceType: 'badge',
  }),
  item({
    id: 'CI-0010',
    name: 'ID Verified',
    complianceType: 'badge',
  }),
  item({
    id: 'CI-0011',
    name: 'Police Check',
    complianceType: 'badge',
  }),
  item({
    id: 'CI-0012',
    name: 'Workers Compensation Insurance',
    complianceType: 'badge',
  }),
  item({
    id: 'CI-0013',
    name: 'CPR',
    complianceType: 'badge',
  }),
  item({
    id: 'CI-0014',
    name: 'Cleaning',
    complianceType: 'badge',
  }),

  // Licences
  item({
    id: 'CI-0003',
    name: 'Electrical',
    complianceType: 'licence',
    applied: true,
    hasSavedConfiguration: true,
    usedInCase: true,
  }),
  item({
    id: 'CI-0004',
    name: 'Plumbing',
    complianceType: 'licence',
    applied: false,
    hasSavedConfiguration: true,
    usedInCase: true,
  }),
  item({
    id: 'CI-0007',
    name: 'Building',
    complianceType: 'licence',
    applied: true,
    hasSavedConfiguration: true,
    usedInCase: false,
  }),
  item({
    id: 'CI-0008',
    name: 'Gas Fitting',
    complianceType: 'licence',
    applied: true,
    hasSavedConfiguration: true,
    usedInCase: true,
  }),
  item({ id: 'CI-0015', name: 'Asbestos Removal', complianceType: 'licence' }),
  item({ id: 'CI-0016', name: 'Demolition', complianceType: 'licence' }),
  item({ id: 'CI-0017', name: 'Pest Control', complianceType: 'licence' }),
  item({ id: 'CI-0018', name: 'Fire Protection', complianceType: 'licence' }),
  item({ id: 'CI-0019', name: 'Waterproofing', complianceType: 'licence' }),
  item({ id: 'CI-0020', name: 'Security Systems', complianceType: 'licence' }),
  item({ id: 'CI-0021', name: 'Refrigeration', complianceType: 'licence' }),
  item({ id: 'CI-0022', name: 'Air Conditioning', complianceType: 'licence' }),
  item({ id: 'CI-0023', name: 'Roof Plumbing', complianceType: 'licence' }),

  // Tickets
  item({ id: 'CI-0005', name: 'White Card', complianceType: 'trade_ticket' }),
  item({
    id: 'CI-0006',
    name: 'Working at Heights',
    complianceType: 'trade_ticket',
    createdOn: '6 Jul 2026',
    lastUpdatedOn: '6 Jul 2026',
  }),
  item({ id: 'CI-0024', name: 'Confined Space', complianceType: 'trade_ticket' }),
  item({ id: 'CI-0025', name: 'Elevated Work Platform', complianceType: 'trade_ticket' }),
  item({ id: 'CI-0026', name: 'Scaffolding', complianceType: 'trade_ticket' }),
  item({ id: 'CI-0027', name: 'Skid Steer', complianceType: 'trade_ticket' }),
  item({ id: 'CI-0028', name: 'Excavation', complianceType: 'trade_ticket' }),
  item({ id: 'CI-0029', name: 'Chainsaw', complianceType: 'trade_ticket' }),
  item({ id: 'CI-0030', name: 'Asbestos Awareness', complianceType: 'trade_ticket' }),
];

export function buildComplianceItemsCatalog() {
  return COMPLIANCE_ITEMS_CATALOG.map((row) => ({ ...row }));
}

export function findComplianceItem(catalog, id) {
  if (!id) return null;
  return catalog.find((row) => row.id === id) ?? null;
}

export function getActiveComplianceItems(catalog) {
  return catalog.filter((row) => normalizeComplianceItemStatus(row.status) === 'active');
}

export function normalizeComplianceItemStatus(status) {
  if (!status) return '';
  return String(status).trim().toLowerCase();
}

export function getComplianceItemStatusLabel(status) {
  const n = normalizeComplianceItemStatus(status);
  if (n === 'active') return 'Active';
  if (n === 'inactive') return 'Inactive';
  return status || '—';
}

export function getComplianceItemTypeLabel(type) {
  const found = COMPLIANCE_ITEM_TYPE_OPTIONS.find((o) => o.value === type);
  return found ? found.label : type || '—';
}
